import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes, HTMLAttributes } from 'react';
import { cx } from './cx.ts';

/**
 * Dispatch Deck table set. Rules (DESIGN.md §6):
 *   - No zebra striping — hairline row dividers + hover tone carry the eye.
 *   - Column headers render at `label` size in `paper-low`; never uppercase CJK.
 *   - Numeric columns are right-aligned mono (`num` prop) — money, counts,
 *     rates are always tabular.
 *   - Row actions stay visible (or fold into DropdownMenu); hover-opacity
 *     reveals are banned.
 */
interface TableProps {
  readonly children: ReactNode;
  readonly stickyHeader?: boolean;
  readonly className?: string;
  /**
   * Utilities for the `<table>` element itself, not the scroll wrapper.
   *
   * The default is `table-layout: auto`, under which a cell's `truncate` does
   * nothing: `max-width` is ignored on table cells in auto layout, so the
   * column still grows to its content's intrinsic width and the wrapper's
   * `overflow-x-auto` turns that into a horizontal scrollbar. A table whose
   * width must not depend on how long its data happens to be passes
   * `table-fixed` here, plus a `min-w-[NNNpx]` floor so the declared column
   * widths stay honest on narrow viewports (#1494).
   *
   * Two spellings coexist on purpose. The older
   * `className="[&>table]:table-fixed"` on the wrapper is a parent-selector
   * variant: it styles the table correctly, but the class never lands ON the
   * `<table>` element, so a test can only re-read the string it passed in — it
   * cannot assert the layout is really in force. Surfaces that need a DOM-level
   * assertion (the routing pool table, whose whole width contract depends on
   * fixed layout) use this prop; the existing wrapper call sites are left alone
   * rather than churned.
   */
  readonly tableClassName?: string;
  readonly testId?: string;
}

export function Table({ children, stickyHeader = false, className, tableClassName, testId }: TableProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={cx('overflow-x-auto rounded-lg border border-line-soft bg-card', stickyHeader && 'max-h-[70vh] overflow-y-auto', className)}
    >
      <table
        className={cx(
          'w-full border-collapse text-dense',
          stickyHeader && '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10',
          tableClassName,
        )}
      >
        {children}
      </table>
    </div>
  );
}

export function THead({ children, className, ...rest }: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return (
    <thead className={cx('bg-card', className)} {...rest}>
      {children}
    </thead>
  );
}

export function TBody({ children, className, ...rest }: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

interface TrProps extends HTMLAttributes<HTMLTableRowElement> {
  readonly interactive?: boolean;
}

export function Tr({ interactive = true, className, children, ...rest }: TrProps): JSX.Element {
  return (
    <tr
      className={cx(interactive && 'transition-colors duration-120 hover:bg-raised/50', '[&:last-child>td]:border-b-0', className)}
      {...rest}
    >
      {children}
    </tr>
  );
}

interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  readonly num?: boolean;
}

export function Th({ num = false, className, children, ...rest }: ThProps): JSX.Element {
  return (
    <th
      scope="col"
      className={cx(
        'border-b border-line px-4 py-2.5 text-label font-medium text-paper-low',
        num ? 'text-right font-mono' : 'text-left',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  readonly num?: boolean;
  readonly mono?: boolean;
  readonly emphasis?: boolean;
}

export function Td({ num = false, mono = false, emphasis = false, className, children, ...rest }: TdProps): JSX.Element {
  return (
    <td
      className={cx(
        'border-b border-line-soft px-4 py-2.5',
        num && 'text-right font-mono',
        !num && mono && 'font-mono',
        emphasis ? 'font-medium text-paper' : 'text-paper-mid',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

interface TableEmptyProps {
  readonly colSpan: number;
  readonly children: ReactNode;
  readonly testId?: string;
}

export function TableEmpty({ colSpan, children, testId }: TableEmptyProps): JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} data-testid={testId} className="px-4 py-12 text-center text-dense text-paper-low">
        {children}
      </td>
    </tr>
  );
}
